import type { SellerRatingSummary } from "@hybrid-escrow/shared";

/** Read-only star display for an average rating (supports half-stars visually via a clipped
 *  overlay). Renders nothing misleading when there's no data yet — "No ratings yet" instead of a
 *  fake zero-star row. */
export function StarRatingDisplay({
  rating,
  count,
  size = "md",
}: {
  rating: SellerRatingSummary;
  count?: boolean;
  size?: "sm" | "md";
}) {
  if (rating.average === null || rating.count === 0) {
    return <span className="text-sm muted">No ratings yet</span>;
  }
  const pct = Math.max(0, Math.min(5, rating.average)) * 20; // 0-100%
  return (
    <span className={`star-rating star-rating--${size}`} title={`${rating.average.toFixed(1)} out of 5`}>
      <span className="star-rating__track" aria-hidden="true">
        ★★★★★
        <span className="star-rating__fill" style={{ width: `${pct}%` }}>
          ★★★★★
        </span>
      </span>
      <span className="text-sm muted">
        {rating.average.toFixed(1)}
        {count !== false ? ` (${rating.count})` : ""}
      </span>
    </span>
  );
}

/** Interactive 1-5 star picker for leaving a review. */
export function StarRatingInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="star-rating-input" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className={`star-rating-input__star ${n <= value ? "is-filled" : ""}`}
          onClick={() => onChange(n)}
        >
          ★
        </button>
      ))}
    </div>
  );
}
